import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { paths } from 'routes/paths';
import ApplicationsIcon from 'assets/ApplicationsIcon';
import {
  registerPendingModalCallback,
  flushPendingOperationNotifications
} from 'screens/Applications&WorkOrders/Components/Notification/genericNotification';
import { SEASON_IDS_PARAM } from 'utilities/cropSeasonUrl';
import {
  resumeApplicationSavedNotificationTimer,
  CREATE_CLASSNAME_PREFIX
} from './Components/Notification';
import useOperationFlags from '../Hooks/useOperationFlags';
import useApplicationsSummary from './Hooks/useApplicationsSummary';
import { OperationType } from '../RouteAdmin';
import { OperationsPageProvider } from '../Components/OperationsPage';
import KeepOrDeleteWOModal from './Components/CreateApplicationFromWorkOrder/components/KeepOrDeleteWOModal';

const ApplicationsPage = () => {
  const { feApplicationsActive } = useOperationFlags();
  const location = useLocation<{
    sourceWorkOrderId?: string;
    createdApplication?: any;
  }>();
  const history = useHistory();

  // Capture into React state on mount so the modal persists even when
  // SummaryParamsContext's useSort effect calls history.replace (without
  // preserving location.state) during its first run on mount.
  const [pendingWorkOrderId] = useState<string | undefined>(
    location.state?.sourceWorkOrderId
  );
  // The Keep/Delete modal and the "Application saved" banner are deferred at
  // creation time (see createNotificationWithPrintListener) and flushed here on
  // mount — so they appear immediately while the applications list + detail view
  // continue loading in the background, rather than waiting for the list to
  // finish loading. useLayoutEffect guarantees the modal callback is registered
  // before the flush (and before any child useEffect fires), so the banner sees
  // the pending modal and stays open beneath it.
  const [showModal, setShowModal] = useState(false);

  useLayoutEffect(() => {
    if (pendingWorkOrderId) {
      registerPendingModalCallback(
        () => setShowModal(true),
        CREATE_CLASSNAME_PREFIX
      );
      flushPendingOperationNotifications();
      return () => registerPendingModalCallback(null);
    }
    flushPendingOperationNotifications();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The crop season carried from the product view (`?seasonIds`) is consumed at
  // load: a new tab reads it via applyUrlCropSeasons, a same-tab click seeds it
  // before navigating. Once here it's stale, so strip it — otherwise a later
  // full refresh would re-apply it over the user's own season selection.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has(SEASON_IDS_PARAM)) {
      return;
    }
    params.delete(SEASON_IDS_PARAM);
    const nextSearch = params.toString();
    history.replace({
      ...location,
      search: nextSearch ? `?${nextSearch}` : ''
    });
  }, [location, history]);

  if (!feApplicationsActive) {
    return <Redirect to={paths.tasks} />;
  }

  return (
    <>
      <OperationsPageProvider
        config={{
          icon: <ApplicationsIcon />,
          title: 'Applications',
          breadcrumbTitle: 'Applications',
          breadcrumbValue: 'All Applications',
          searchPlaceholder: 'Search applications',
          emptyMessage: 'No Applications found.',
          emptyStateTitle: 'No Applications Found',
          emptyStateMessage:
            'Create an Application or change the selected season.',
          createButtonLabel: 'Create Application',
          cropZoneMessage:
            'Create a crop zone to proceed with adding Applications.',
          operationType: OperationType.APPLICATION,
          createPath: paths.applications,
          storageKey: 'applications'
        }}
        useSummaryHook={useApplicationsSummary}
      />
      {showModal && pendingWorkOrderId && (
        <KeepOrDeleteWOModal
          workOrderId={pendingWorkOrderId}
          onClose={() => {
            resumeApplicationSavedNotificationTimer();
            setShowModal(false);
          }}
          onError={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default ApplicationsPage;
